import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Phone, Sparkles, CheckCircle2, ChevronDown, ArrowRight, Loader2 } from "lucide-react";
import CoverageMap from "@/components/site/CoverageMap";
import { CITIES, CITY_COORDS, CONTACT } from "@/lib/data";
import { useI18n } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ServiceAreaCheck() {
  const { t } = useI18n();
  const [city, setCity] = useState("");
  const [checked, setChecked] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const coordFor = (n) => CITY_COORDS.find((c) => c.name === n);
  const focus = checked ? coordFor(city) : null;
  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const check = () => {
    if (!city) { toast.error("Please select your city first."); return; }
    setChecked(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email) {
      toast.error("Please add your name, phone and email.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/quote`, {
        name: form.name, business: form.name, email: form.email, phone: form.phone,
        service: "Business Waste Pickup", container_size: "",
        message: `Service-area check — ${city}. Requested a free quote.`,
      });
      setDone(true);
      toast.success("Thanks! One of our experts will call you shortly.");
    } catch {
      toast.error("Something went wrong. Please call us at " + CONTACT.phone);
    } finally { setLoading(false); }
  };

  const inputCls = "w-full px-4 py-3.5 rounded-xl bg-white border border-[#E7E5DF] text-[#0B0F14] placeholder:text-slate-400 focus:outline-none focus:border-emerald-600/60 transition-colors text-sm";

  return (
    <section id="coverage" className="relative bg-[#FBFAF8] py-24" data-testid="service-area-check">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
        <div className="max-w-3xl mb-12">
          <h2 className="font-display font-extrabold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-[#0B0F14]">
            {t("sac.title")}
          </h2>
          <p className="mt-5 text-slate-600 leading-relaxed">
{t("sac.body")}
          </p>
        </div>

        <div className="grid lg:grid-cols-[1.05fr_0.95fr] gap-8 items-start">
          {/* Capture card */}
          <div className="min-w-0 bg-white border border-[#E7E5DF] rounded-3xl shadow-soft p-6 sm:p-8">
            <h3 className="flex items-center gap-2 font-display font-bold text-xl sm:text-2xl text-[#0B0F14]">
              <MapPin size={22} className="text-emerald-600" /> {t("sac.check")}
            </h3>

            <div className="mt-5 flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <select value={city} onChange={(e) => { setCity(e.target.value); setChecked(false); setDone(false); }}
                  data-testid="sac-city-select"
                  className={`${inputCls} appearance-none pr-10 ${city ? "text-[#0B0F14]" : "text-slate-400"}`}>
                  <option value="" label={t("sac.selectCity")} />
                  {CITIES.map((c) => <option key={c} value={c} label={`${c}, FL, USA`} className="text-[#0B0F14]" />)}
                </select>
                <ChevronDown size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
              </div>
              <button onClick={check} data-testid="sac-check-btn"
                className="px-6 py-3.5 rounded-xl border border-[#E7E5DF] bg-[#F0EFEA] text-[#0B0F14] font-semibold text-sm hover:border-emerald-600/50 transition-colors whitespace-nowrap">
                {t("sac.checkBtn")}
              </button>
            </div>

            <AnimatePresence mode="wait">
              {checked && !done && (
                <motion.div key="panel" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                  className="mt-6 rounded-2xl border border-emerald-600/25 bg-emerald-600/[0.06] p-5 sm:p-6">
                  <p className="flex items-center gap-2 font-display font-bold text-lg text-[#0B0F14]">
                    <Sparkles size={18} className="text-emerald-600" /> {t("sac.cutting1")} {city} {t("sac.cutting2")}
                  </p>
                  <p className="text-slate-600 text-sm mt-1">
                    {t("sac.ensures1")} {city} {t("sac.ensures2")}
                  </p>
                  <p className="mt-3 flex items-center gap-1.5 text-emerald-700 text-sm font-medium">
                    <CheckCircle2 size={15} /> {t("sac.available1")} {city}.
                  </p>

                  <form onSubmit={submit} className="mt-5 grid sm:grid-cols-3 gap-3" data-testid="sac-form">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1.5">{t("sac.name")}</label>
                      <input className={inputCls} placeholder={t("sac.namePh")} value={form.name} onChange={update("name")} data-testid="sac-name" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1.5">{t("sac.phone")}</label>
                      <input className={inputCls} placeholder="(000) 000-0000" value={form.phone} onChange={update("phone")} data-testid="sac-phone" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1.5">{t("sac.email")}</label>
                      <input type="email" className={inputCls} placeholder={t("sac.emailPh")} value={form.email} onChange={update("email")} data-testid="sac-email" />
                    </div>

                    <p className="sm:col-span-3 text-[11px] text-slate-400 leading-relaxed">
                      {t("sac.consent1")} <span className="font-semibold text-slate-500">&quot;{t("sac.getQuoteShort")}&quot;</span>{t("sac.consent2")}
                    </p>

                    <button type="submit" disabled={loading} data-testid="sac-submit"
                      className="sm:col-span-3 flex items-center justify-center gap-2 px-6 py-4 rounded-xl bg-[#0E3B3B] text-white font-bold hover:bg-[#0A2C2C] active:scale-[0.99] transition-all disabled:opacity-60">
                      {loading ? <><Loader2 size={18} className="animate-spin" /> {t("sac.sending")}</> : <>{t("sac.getQuote")} <ArrowRight size={18} /></>}
                    </button>
                  </form>
                </motion.div>
              )}

              {done && (
                <motion.div key="done" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                  className="mt-6 rounded-2xl border border-emerald-600/25 bg-emerald-600/[0.06] p-8 text-center" data-testid="sac-success">
                  <CheckCircle2 size={48} className="text-emerald-600 mx-auto mb-4" />
                  <h4 className="font-display font-bold text-xl text-[#0B0F14]">{t("sac.allSet")}</h4>
                  <p className="text-slate-600 text-sm mt-2 max-w-sm mx-auto">
                    {t("sac.thanks1")} {form.name || ""} {t("sac.thanks2")} {city}.
                  </p>
                </motion.div>
              )}
            </AnimatePresence>

            <p className="mt-6 text-center text-sm text-slate-500">
              Prefer to talk directly? Call our EWS expert at{" "}
              <a href={CONTACT.phoneHref} className="inline-flex items-center gap-1.5 font-semibold text-emerald-700 hover:text-emerald-800" data-testid="sac-call">
                <Phone size={14} /> {CONTACT.phone}
              </a>
            </p>
          </div>

          {/* Map */}
          <motion.div className="min-w-0" initial={{ opacity: 0, scale: 0.97 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <CoverageMap focus={focus} />
            <p className="mt-4 border-l-2 border-emerald-600 pl-3 font-mono-tech text-[10px] uppercase tracking-[0.18em] text-slate-500" data-testid="sac-map-caption">
              Serving {CITIES.length} Miami-Dade communities · select a city to locate it
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
